import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Navbar.css";
import { ImagesApp } from "../../assets/ImagesApp";
import {
  FaPhoneAlt,
  FaGlobe,
  FaCode,
  FaMobileAlt,
  FaSearch,
  FaLaptopCode,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaClock,
  FaMapMarkerAlt,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaShoppingCart,
  FaRobot,
  FaCloud,
  FaTools,
  FaPaintBrush,
  FaGraduationCap,
  FaDatabase,
  FaServer,
  FaChartLine
} from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024); // ब्रेकप्वाइंट 1024px
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTopBarVisible, setIsTopBarVisible] = useState(true);
  const menuRef = useRef(null);
  const location = useLocation();

  // Define navigation data
  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about-us", label: "About" }
  ];

  const servicesItems = [
    { path: "/web-design", label: "UI/UX & Web Design", icon: <FaGlobe /> },
    { path: "/web-development-service-company-lucknow", label: "Web Development", icon: <FaCode /> },
    { path: "/mobile-app-development-2", label: "Mobile App Development", icon: <FaMobileAlt /> },
    { path: "/seo-lucknow", label: "Digital Marketing & SEO", icon: <FaSearch /> },
    { path: "/custom-software", label: "Custom Software Development", icon: <FaCode /> },
    { path: "/ecommerce-website-development", label: "E-commerce Website", icon: <FaShoppingCart /> },
    { path: "/ai-software-development-services", label: "AI & ML Development", icon: <FaRobot /> },
    { path: "/cloud-and-devops", label: "Cloud & DevOps", icon: <FaCloud /> },
    { path: "/maintenance-services", label: "Maintenance Services", icon: <FaTools /> },
    { path: "/website-redesign", label: "Website Re-design", icon: <FaPaintBrush /> },
    { path: "/software-training", label: "Software Training", icon: <FaLaptopCode /> }
  ];

  const trainingItems = [
    { path: "/uiux-training", label: "UI/UX Design Training", icon: <FaGlobe /> },
    { path: "/frontend-training", label: "Frontend Development", icon: <FaCode /> },
    { path: "/backend-training", label: "Backend Development", icon: <FaServer /> },
    { path: "/fullstack-training", label: "Full Stack Development", icon: <FaLaptopCode /> },
    { path: "/mobile-app-training", label: "Mobile App Development", icon: <FaMobileAlt /> },
    { path: "/data-science-training", label: "Data Science", icon: <FaDatabase /> },
    { path: "/dsa-training", label: "DSA & Algorithms", icon: <FaChartLine /> },
    { path: "/seo-digital-training", label: "SEO & Digital Marketing", icon: <FaSearch /> }
  ];

  const additionalLinks = [
    { path: "/contactus", label: "Contact" },
    { path: "/career", label: "Career" },
    { path: "/blogs", label: "Blogs" }
  ];

  const contactInfo = {
    phoneNumbers: [
      { number: "+91 6387722743", whatsapp: "916387722743" },
      { number: "+91 9889685276", whatsapp: "919889685276" },
      { number: "+91 8090387120", whatsapp: "918090387120" }
    ],
    location: "Varanasi, Uttar Pradesh",
    hours: "Mon - Sat: 10:00 AM - 6:00 PM"
  };

  const socialLinks = [
    { url: "https://www.facebook.com/p/infodeltasys-100077470740495/", icon: <FaFacebook />, label: "Facebook" },
    { url: "https://www.facebook.com/share/17umz2P82J/", icon: <FaTwitter />, label: "Twitter" },
    { url: "https://www.instagram.com/infodeltasys_global/", icon: <FaInstagram />, label: "Instagram" },
    { url: "https://linkedin.com", icon: <FaLinkedin />, label: "LinkedIn" },
    { url: "https://youtube.com", icon: <FaYoutube />, label: "YouTube" }
  ];

  // Check if current path matches dropdown items
  const isServicesActive = servicesItems.some(item => location.pathname.startsWith(item.path));
  const isTrainingActive = trainingItems.some(item => location.pathname.startsWith(item.path));

  // Toggle handlers
  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
    if (isMenuOpen) {
      setOpenDropdown(null);
    }
  };

  const toggleDropdown = useCallback((dropdownName) => {
    if (!isDesktop) {
      setOpenDropdown(prev => prev === dropdownName ? null : dropdownName);
    }
  }, [isDesktop]);

  const handleDropdownHover = useCallback((dropdownName, show) => {
    if (isDesktop) {
      setOpenDropdown(show ? dropdownName : null);
    }
  }, [isDesktop]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        if (isMenuOpen && !isDesktop) {
          closeMenu();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen, isDesktop, closeMenu]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setIsMenuOpen(false);
        setOpenDropdown(null);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      setIsTopBarVisible(window.scrollY <= 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open on mobile
  useEffect(() => {
    if (isMenuOpen && !isDesktop) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen, isDesktop]);

  return (
    <>
      {/* Top Contact Bar */}
      {isDesktop && (
        <div className={`navbar-top-contact-bar ${!isTopBarVisible ? 'navbar-hidden' : ''}`}>
          <div className="navbar-top-bar-container">
            <div className="navbar-top-bar-left">
              <div className="navbar-contact-info">
                <div className="navbar-contact-item">
                  <FaClock className="navbar-contact-icon" />
                  <span>{contactInfo.hours}</span>
                </div>
                <div className="navbar-contact-item">
                  <FaMapMarkerAlt className="navbar-contact-icon" />
                  <span>{contactInfo.location}</span>
                </div>
                <div className="navbar-phone-numbers">
                  {contactInfo.phoneNumbers.map((phone, index) => (
                    <div className="navbar-phone-item" key={index}>
                      <FaPhoneAlt className="navbar-phone-icon" />
                      <span>{phone.number}</span>
                      <a
                        href={`https://wa.me/${phone.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`WhatsApp ${phone.number}`}
                        className="navbar-whatsapp-link"
                      >
                        <FaWhatsapp className="navbar-whatsapp-icon" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="navbar-top-bar-right">
              <div className="navbar-social-media-icons">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    className="navbar-social-icon"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Navbar */}
      <header className={`navbar-main ${isScrolled ? 'navbar-scrolled' : ''}`} role="banner">
        <div className="navbar-container">
          <NavLink
            to="/"
            className="navbar-logo"
            onClick={closeMenu}
            aria-label="Home"
          >
            <img
              src={isScrolled ? ImagesApp.Logocompany1 : ImagesApp.Logocompany}
              alt="InfoDeltaSys - Best Software, Website & Training Company"
              className="navbar-logo-image"
            />
          </NavLink>

          <button
            className="navbar-toggle"
            onClick={toggleMenu}
            aria-label="Toggle Menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav
            ref={menuRef}
            className={`navbar-links ${isMenuOpen ? "navbar-open" : ""}`}
            role="navigation"
            aria-label="Primary Navigation"
          >
            {/* Mobile Menu Header */}
            {!isDesktop && (
              <div className="navbar-mobile-menu-header">
                <NavLink to="/" className="navbar-mobile-logo" onClick={closeMenu}>
                  <img src={ImagesApp.Logocompany1} alt="InfoDeltaSys Logo" />
                </NavLink>
              </div>
            )}

            {/* Desktop Navigation */}
            {isDesktop ? (
              <div className="navbar-desktop-nav-links">
                {navLinks.map((link, index) => (
                  <NavLink
                    key={index}
                    to={link.path}
                    className={({ isActive }) => `navbar-nav-link ${isActive ? "navbar-active" : ""}`}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                ))}

                {/* Services Dropdown - Desktop */}
                <div
                  className="navbar-dropdown-container navbar-services-dropdown"
                  onMouseEnter={() => handleDropdownHover('services', true)}
                  onMouseLeave={() => handleDropdownHover('services', false)}
                >
                  <button
                    className={`navbar-dropdown-toggle ${isServicesActive ? "navbar-active" : ""}`}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'services'}
                  >
                    Services
                  </button>
                  {openDropdown === 'services' && (
                    <div className="navbar-dropdown-content navbar-services-dropdown-content">
                      {servicesItems.map((item, index) => (
                        <NavLink
                          key={index}
                          to={item.path}
                          className={({ isActive }) => `navbar-dropdown-item ${isActive ? "navbar-active" : ""}`}
                          onClick={closeMenu}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>

                {/* Training Dropdown - Desktop */}
                <div
                  className="navbar-dropdown-container navbar-training-dropdown"
                  onMouseEnter={() => handleDropdownHover('training', true)}
                  onMouseLeave={() => handleDropdownHover('training', false)}
                >
                  <button
                    className={`navbar-dropdown-toggle ${isTrainingActive ? "navbar-active" : ""}`}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'training'}
                  >
                    Training
                  </button>
                  {openDropdown === 'training' && (
                    <div className="navbar-dropdown-content navbar-training-dropdown-content">
                      {trainingItems.map((item, index) => (
                        <NavLink
                          key={index}
                          to={item.path}
                          className={({ isActive }) => `navbar-dropdown-item ${isActive ? "navbar-active" : ""}`}
                          onClick={closeMenu}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>

                {additionalLinks.map((link, index) => (
                  <NavLink
                    key={index}
                    to={link.path}
                    className={({ isActive }) => `navbar-nav-link ${isActive ? "navbar-active" : ""}`}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              /* Mobile Navigation */
              <div className="navbar-mobile-nav-links">
                {navLinks.map((link, index) => (
                  <NavLink
                    key={index}
                    to={link.path}
                    className={({ isActive }) => `navbar-mobile-nav-link ${isActive ? "navbar-active" : ""}`}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                ))}

                {/* Services Dropdown - Mobile */}
                <div className="navbar-mobile-dropdown-container">
                  <button
                    className={`navbar-mobile-dropdown-toggle ${isServicesActive ? "navbar-active" : ""}`}
                    onClick={() => toggleDropdown('services')}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'services'}
                  >
                    Services
                    <span className="navbar-dropdown-arrow">
                      {openDropdown === 'services' ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </button>
                  <div className={`navbar-mobile-dropdown-content ${openDropdown === 'services' ? "navbar-show" : ""}`}>
                    {servicesItems.map((item, index) => (
                      <NavLink
                        key={index}
                        to={item.path}
                        className={({ isActive }) => `navbar-mobile-dropdown-item ${isActive ? "navbar-active" : ""}`}
                        onClick={closeMenu}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>

                {/* Training Dropdown - Mobile */}
                <div className="navbar-mobile-dropdown-container">
                  <button
                    className={`navbar-mobile-dropdown-toggle ${isTrainingActive ? "navbar-active" : ""}`}
                    onClick={() => toggleDropdown('training')}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'training'}
                  >
                    Training
                    <span className="navbar-dropdown-arrow">
                      {openDropdown === 'training' ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </button>
                  <div className={`navbar-mobile-dropdown-content ${openDropdown === 'training' ? "navbar-show" : ""}`}>
                    {trainingItems.map((item, index) => (
                      <NavLink
                        key={index}
                        to={item.path}
                        className={({ isActive }) => `navbar-mobile-dropdown-item ${isActive ? "navbar-active" : ""}`}
                        onClick={closeMenu}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>

                {additionalLinks.map((link, index) => (
                  <NavLink
                    key={index}
                    to={link.path}
                    className={({ isActive }) => `navbar-mobile-nav-link ${isActive ? "navbar-active" : ""}`}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                ))}

                {/* Mobile Contact Info */}
                <div className="navbar-mobile-contact-info">
                  <div className="navbar-mobile-contact-item">
                    <FaPhoneAlt className="navbar-mobile-contact-icon" />
                    <div className="navbar-mobile-contact-details">
                      {contactInfo.phoneNumbers.map((phone, index) => (
                        <a key={index} href={`tel:${phone.number.replace(/\s+/g, '')}`}>
                          {phone.number}
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="navbar-mobile-contact-item">
                    <FaMapMarkerAlt className="navbar-mobile-contact-icon" />
                    <span>{contactInfo.location}</span>
                  </div>
                  <div className="navbar-mobile-contact-item">
                    <FaClock className="navbar-mobile-contact-icon" />
                    <span>{contactInfo.hours}</span>
                  </div>
                  <div className="navbar-mobile-social-icons">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
      </header>
    </>
  );
}

export default Navbar;